/**
 * What the builder knows, with no React in it.
 *
 * Everything here is derived from `GET /schema`. There is no list of types, no
 * list of fields and no list of what a field may be used for in this file: a
 * type added to the kernel appears in the builder with no change here, which is
 * the rule `packages/kernel-ui/README.md` calls "render from the kernel".
 *
 * ## The kernel decides; this keeps the form honest
 *
 * `insights_query.parse` in the kernel is the authority on whether a spec is
 * allowed, and it refuses a bad one with the part named. The rules below mirror
 * it so the form never *offers* what will be refused: a field flagged
 * `aggregate_only` is not in the group-by list, a sum is not offered on text.
 * Mirroring is a convenience and nothing here is a check anyone relies on. A
 * person who edits the request by hand reaches the kernel's rule, not this.
 *
 * ## What this never does
 *
 * It never puts a tag, a scope or a grant in a spec. A spec names what is asked;
 * what the person may open is decided by the kernel from their token, and a
 * spec with anything else in it is refused.
 */

export const FUNCTION_LABEL = {
  count: "Count",
  count_distinct: "Count of different",
  sum: "Total",
  avg: "Average",
  min: "Lowest",
  max: "Highest",
};

export const BUCKET_LABEL = {
  day: "day", week: "week", month: "month", quarter: "quarter", year: "year",
};

const OPS_NUMBER = ["eq", "ne", "gt", "gte", "lt", "lte", "is_set", "is_not_set"];
const OPS_TIME = OPS_NUMBER;
const OPS_TEXT = ["eq", "ne", "in", "is_set", "is_not_set"];

const OP_LABEL = {
  text: { eq: "is", ne: "is not", in: "is one of", is_set: "has a value", is_not_set: "has no value" },
  number: {
    eq: "is", ne: "is not", gt: "is more than", gte: "is at least", lt: "is less than",
    lte: "is at most", is_set: "has a value", is_not_set: "has no value",
  },
  time: {
    eq: "is", ne: "is not", gt: "is after", gte: "is on or after", lt: "is before",
    lte: "is on or before", is_set: "has a value", is_not_set: "has no value",
  },
};

export const NONE_LABEL = "(none)";
export const MAX_SERIES = 4;
export const MAX_DIMENSIONS = 2;

/** `text`, `number`, `time` or `composite` (a list or an object). */
export function kindOf(field) {
  if (!field) return "text";
  if (field.repeated || field.type === "object") return "composite";
  if (field.type === "integer") return "number";
  if (field.type === "timestamp" || field.format === "iso8601" || field.format === "date-time") {
    return "time";
  }
  return "text";
}

/** `open`, `no_group_by` or `aggregate_only`, from the flags `/schema` serves. */
export function exposureOf(field) {
  if (field?.aggregate_only) return "aggregate_only";
  if (field?.no_group_by) return "no_group_by";
  return "open";
}

export function humanise(name) {
  const text = String(name || "").replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The types a chart may be about: every type the kernel serves except one it says is not
 * chartable (`Report`, whose readers are a list on the record and not tags). Said by the
 * kernel in `GET /schema`, so a type that joins it is in or out with no change here.
 */
export const typesOf = (schema) => [...(schema?.types || [])]
  .filter((t) => t.chartable !== false)
  .sort((a, b) => a.type.localeCompare(b.type));

export const typeOf = (schema, name) => (schema?.types || []).find((t) => t.type === name);

export const fieldsOf = (schema, name) => typeOf(schema, name)?.fields || [];

export const fieldOf = (schema, typeName, fieldName) =>
  fieldsOf(schema, typeName).find((f) => f.name === fieldName);

/** What a measure may be of this field. `field` empty means "records". */
export function measureFunctions(field) {
  if (!field) return ["count"];
  const kind = kindOf(field);
  if (kind === "composite") return ["count"];
  const restricted = exposureOf(field) === "aggregate_only";
  if (kind === "number") {
    return restricted
      ? ["count", "count_distinct", "sum", "avg"]
      : ["count", "count_distinct", "sum", "avg", "min", "max"];
  }
  if (kind === "time") {
    return restricted ? ["count", "count_distinct"] : ["count", "count_distinct", "min", "max"];
  }
  return ["count", "count_distinct"];
}

/** Fields a chart may be grouped by: open, and neither a list nor an object. */
export const dimensionFields = (fields) =>
  fields.filter((f) => exposureOf(f) === "open" && kindOf(f) !== "composite");

const PREFERRED_GROUPINGS = ["status", "category", "direction", "type", "kind", "currency", "source"];

/**
 * Something to break a count down by, so a chart has something to draw (`{ field, bucket }`, or `null`).
 *
 * A count of everything is one figure and has nothing to put on an axis, so asking for bars or a line of it draws
 * nothing and the person is left wondering why. This is the grouping to offer: for a line, the first date, by month;
 * otherwise a field that names a state or a kind (status, category, direction) before any other text field, and never
 * a number, which is a measure, or a list.
 */
export function suggestGrouping(fields, chart = "bar") {
  const ok = dimensionFields(fields);
  const times = ok.filter((f) => kindOf(f) === "time");
  if (chart === "line" && times.length) return { field: times[0].name, bucket: "month" };
  const texts = ok.filter((f) => kindOf(f) === "text");
  const pick = PREFERRED_GROUPINGS.map((n) => texts.find((f) => f.name === n)).find(Boolean)
    || texts.find((f) => Array.isArray(f.enum) && f.enum.length)
    || texts[0];
  if (pick) return { field: pick.name, bucket: "" };
  if (times.length) return { field: times[0].name, bucket: "month" };
  return null;
}

/**
 * The fields to offer as one-click plots, in the order a person is likely to want them: dates first, then the fields that
 * name a state or a kind, then any other text. Never a number (a measure), a list, or a field the kernel will not group by.
 */
export function quickPlots(fields) {
  const ok = dimensionFields(fields);
  const times = ok.filter((f) => kindOf(f) === "time");
  const texts = ok.filter((f) => kindOf(f) === "text");
  const named = PREFERRED_GROUPINGS.map((n) => texts.find((f) => f.name === n)).filter(Boolean);
  const enums = texts.filter((f) => !named.includes(f) && Array.isArray(f.enum) && f.enum.length);
  const other = texts.filter((f) => !named.includes(f) && !enums.includes(f));
  return [...times, ...named, ...enums, ...other];
}

/** Fields a filter may name: anything but aggregate-only, a list or an object. */
export const filterFields = (fields) =>
  fields.filter((f) => exposureOf(f) !== "aggregate_only" && kindOf(f) !== "composite");

export function opsFor(field) {
  const kind = kindOf(field);
  return kind === "number" ? OPS_NUMBER : kind === "time" ? OPS_TIME : OPS_TEXT;
}

/**
 * The operator a new filter starts with. Nobody asks for the records where an amount is exactly 10,000 or a date is exactly
 * one instant, so a number starts at "is more than" and a date at "is on or after"; text starts at "is".
 */
export function defaultOp(field) {
  const kind = kindOf(field);
  return kind === "number" ? "gt" : kind === "time" ? "gte" : opsFor(field)[0];
}

export function opLabel(field, op) {
  const kind = kindOf(field);
  return (OP_LABEL[kind] || OP_LABEL.text)[op] || op;
}

export const opTakesValue = (op) => op !== "is_set" && op !== "is_not_set";

// -- state ------------------------------------------------------------------

export function newState(type) {
  return {
    type,
    measures: [{ fn: "count", field: "" }],
    dimensions: [],
    filters: [],
    chart: "auto",
  };
}

const CURRENCY_EXPONENT = { JPY: 0, KRW: 0, VND: 0, CLP: 0, ISK: 0, UGX: 0, PYG: 0, XAF: 0, XOF: 0, BHD: 3, KWD: 3, OMR: 3, JOD: 3, TND: 3 };

/** Digits after the point in a currency's minor unit: 2 unless it is one of the few that differ. */
export const exponentOf = (currency) => CURRENCY_EXPONENT[String(currency || "").trim().toUpperCase()] ?? 2;

/** `10k`, `10,000`, `2,500.50` or `1.5m`: an amount written the way a person says it, in whole units, or NaN. */
export function parseAmount(text) {
  const m = /^(-?)\s*([\d,]*\.?\d+)\s*([kKmM]?)$/.exec(String(text ?? "").trim());
  if (!m) return NaN;
  const n = Number(m[2].replace(/,/g, ""));
  if (!Number.isFinite(n)) return NaN;
  return (m[1] ? -1 : 1) * n * ({ k: 1e3, m: 1e6 }[m[3].toLowerCase()] || 1);
}

/** The cents a money filter is in: the currency the form pinned with `is`, else two digits. */
function exponentFor(state, field) {
  const pin = field?.currency_field
    && state.filters.find((f) => f.field === field.currency_field && f.op === "eq" && String(f.value ?? "").trim());
  return pin ? exponentOf(pin.value) : 2;
}

/**
 * One filter value, as the form holds it (text), turned into what the kernel
 * wants. `{ ok: false }` means the row is not finished or not valid, and the
 * form says so beside it rather than sending something that will be refused.
 */
export function parseValue(field, op, raw, ctx = {}) {
  if (!opTakesValue(op)) return { ok: true, value: undefined };
  const kind = kindOf(field);
  const one = (text) => {
    const s = String(text ?? "").trim();
    if (!s) return { ok: false, why: "needs a value" };
    if (kind === "number") {
      if (field?.minor_units && ctx.unit === "major") {
        // An amount as a person says it: 10k, 10,000 or 2,500.50. The schema holds minor units; the currency sets the cents.
        const n = parseAmount(s);
        if (Number.isNaN(n)) return { ok: false, why: "needs an amount, like 10000, 2,500.50 or 10k" };
        return { ok: true, value: Math.round(n * 10 ** (ctx.exponent ?? 2)) };
      }
      if (!/^-?\d+$/.test(s)) return { ok: false, why: "needs a whole number" };
      return { ok: true, value: Number(s) };
    }
    if (kind === "time") {
      return Number.isNaN(Date.parse(s))
        ? { ok: false, why: "needs a date, like 2026-09-30" }
        : { ok: true, value: s };
    }
    return { ok: true, value: s };
  };
  if (op !== "in") return one(raw);
  const parts = String(raw ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  if (!parts.length) return { ok: false, why: "needs at least one value" };
  const parsed = parts.map(one);
  return parsed.every((p) => p.ok)
    ? { ok: true, value: parsed.map((p) => p.value) }
    : parsed.find((p) => !p.ok);
}

/**
 * A spec, as the form holds it: what an asked-for report opens as.
 *
 * `leftOut` names what a spec can say and the form cannot (an order and a limit), so the
 * screen says so and the person is not left believing the form is the whole of what was
 * proposed. A value goes back to text, which is what the form edits and `parseValue` reads.
 */
export function stateFromSpec(spec, schema) {
  const text = (v) => (Array.isArray(v) ? v.join(", ") : String(v ?? ""));
  const fields = schema ? fieldsOf(schema, spec.type) : [];
  // A money filter in a spec is in minor units; the form holds it in whole units, as a person would write it.
  const filter = (f) => {
    const money = fields.find((x) => x.name === f.field && x.minor_units);
    if (!money || !opTakesValue(f.op)) return { field: f.field, op: f.op, value: opTakesValue(f.op) ? text(f.value) : "" };
    const pin = (spec.filters || []).find((x) => x.field === money.currency_field && x.op === "eq");
    const whole = (v) => String(Number(v) / 10 ** exponentOf(pin?.value));
    return { field: f.field, op: f.op, unit: "major", value: Array.isArray(f.value) ? f.value.map(whole).join(", ") : whole(f.value) };
  };
  const state = {
    type: spec.type,
    measures: (spec.measures || []).map((m) => ({ fn: m.fn, field: m.field || "" })),
    dimensions: (spec.dimensions || []).map((d) => ({ field: d.field, bucket: d.bucket || "" })),
    filters: (spec.filters || []).map(filter),
    chart: "auto",
    ...(spec.source ? { source: spec.source } : {}),
    ...(spec.linked ? { linked: spec.linked } : {}),
  };
  if (!state.measures.length) state.measures = [{ fn: "count", field: "" }];
  const leftOut = [];
  if ((spec.order || []).length) leftOut.push("the ordering");
  if (spec.limit) leftOut.push("the limit");
  return { state, leftOut };
}

/** The spec the kernel is asked, from the form. Unfinished rows are left out. */
export function buildSpec(state, schema) {
  const fields = fieldsOf(schema, state.type);
  const by = Object.fromEntries(fields.map((f) => [f.name, f]));
  const spec = { type: state.type, measures: [] };
  for (const m of state.measures) {
    if (m.fn === "count" && !m.field) spec.measures.push({ fn: "count" });
    else if (m.field && by[m.field]) spec.measures.push({ fn: m.fn, field: m.field });
  }
  const dimensions = state.dimensions
    .filter((d) => d.field && by[d.field])
    .map((d) => (d.bucket ? { field: d.field, bucket: d.bucket } : { field: d.field }));
  if (dimensions.length) spec.dimensions = dimensions;
  const filters = [];
  for (const f of state.filters) {
    const field = by[f.field];
    if (!field) continue;
    const parsed = parseValue(field, f.op, f.value, { unit: f.unit, exponent: exponentFor(state, field) });
    if (!parsed.ok) continue;
    filters.push(opTakesValue(f.op)
      ? { field: f.field, op: f.op, value: parsed.value }
      : { field: f.field, op: f.op });
  }
  if (filters.length) spec.filters = filters;
  // Two things the form does not edit but a saved report may carry (LO-227): only records one source wrote,
  // and only those with an accepted link to a record another source wrote. They ride along so opening a
  // report and updating it does not quietly take them off.
  if (state.source) spec.source = state.source;
  if (state.linked) spec.linked = state.linked;
  return spec;
}

// -- what is wrong before it is sent -----------------------------------------

/** The currency field a measure needs said, if any. */
function currencyNeeded(state, schema) {
  const fields = fieldsOf(schema, state.type);
  for (const m of state.measures) {
    const field = fields.find((f) => f.name === m.field);
    if (field?.minor_units && field.currency_field
        && ["sum", "avg", "min", "max"].includes(m.fn)) {
      return field.currency_field;
    }
  }
  return "";
}

export function hasCurrency(state, currency) {
  return state.dimensions.some((d) => d.field === currency && !d.bucket)
    || state.filters.some((f) => f.field === currency && f.op === "eq" && String(f.value || "").trim());
}

/**
 * What a person should be told before they press Run, each with the one change
 * that fixes it, so the screen can offer it as a button instead of a lecture.
 */
export function problems(state, schema) {
  const out = [];
  const currency = currencyNeeded(state, schema);
  if (currency && !hasCurrency(state, currency)) {
    out.push({
      code: "money_needs_currency",
      text: `Money only adds up within one currency, so this needs to say which: split it by ${humanise(currency).toLowerCase()} or filter to one.`,
      fix: { label: `Split by ${humanise(currency).toLowerCase()}`, field: currency },
    });
  }
  for (const f of state.filters) {
    const field = fieldOf(schema, state.type, f.field);
    if (!field || !opTakesValue(f.op) || f.value === "" || f.value == null) continue;
    const parsed = parseValue(field, f.op, f.value, { unit: f.unit, exponent: exponentFor(state, field) });
    if (!parsed.ok) out.push({ code: "bad_filter", text: `${humanise(f.field)} ${parsed.why}.` });
  }
  if (state.linked?.source && state.linked.source === state.source) {
    out.push({ code: "same_source", text: "A record is not linked to a record of its own connector: pick another connector to be also held by, or none." });
  }
  const spec = buildSpec(state, schema);
  if (!spec.measures.length) out.push({ code: "no_measure", text: "Pick something to count or add up." });
  return out;
}

export function withCurrencySplit(state, currency) {
  if (state.dimensions.length >= MAX_DIMENSIONS) return state;
  return { ...state, dimensions: [...state.dimensions, { field: currency, bucket: "" }] };
}

// -- describing it --------------------------------------------------------------

export function describeMeasure(m, schema, typeName) {
  const field = m.field ? fieldOf(schema, typeName, m.field) : null;
  if (!field) return "Count of records";
  const what = humanise(field.name).toLowerCase();
  return m.fn === "count" ? `Count of ${what}` : `${FUNCTION_LABEL[m.fn]} ${what}`;
}

export function describeDimension(d) {
  const name = humanise(d.field).toLowerCase();
  return d.bucket ? `${name} by ${BUCKET_LABEL[d.bucket]}` : name;
}

/** "Total amount, by issued at by month and currency, where status is paid." */
export function describe(state, schema) {
  const measures = state.measures
    .filter((m) => m.field || m.fn === "count")
    .map((m) => describeMeasure(m, schema, state.type).toLowerCase());
  const lead = measures.length ? measures.join(" and ") : "records";
  const head = `${humanise(lead)} in ${state.type}`;
  const by = state.dimensions.filter((d) => d.field).map(describeDimension);
  const where = state.filters.filter((f) => f.field).map((f) => {
    const field = fieldOf(schema, state.type, f.field);
    const op = opLabel(field, f.op);
    return opTakesValue(f.op) && f.value !== "" ? `${humanise(f.field).toLowerCase()} ${op} ${f.value}`
      : opTakesValue(f.op) ? null : `${humanise(f.field).toLowerCase()} ${op}`;
  }).filter(Boolean);
  return [head, by.length ? `by ${by.join(" and ")}` : "", where.length ? `where ${where.join(" and ")}` : "", describeLink(state)]
    .filter(Boolean).join(", ");
}

/** `written by xero, also held by google through an accepted link`: what a report carries that the form does not edit. */
export function describeLink(state) {
  const from = state.source ? `written by ${state.source}` : "";
  const to = state.linked?.source ? `${state.source ? "also " : ""}held by ${state.linked.source} through an accepted link` : "";
  return [from, to].filter(Boolean).join(", ");
}

// -- the answer ----------------------------------------------------------------------

const isMoney = (col) => col?.type === "money";

/** A cell, in words. Money arrives in minor units and leaves in major ones. */
export function formatCell(col, value, row = {}) {
  if (value === null || value === undefined) return NONE_LABEL;
  if (isMoney(col)) {
    const currency = col.currency || row[col.currency_field];
    if (!currency || currency === NONE_LABEL) return Number(value).toLocaleString();
    try {
      const digits = new Intl.NumberFormat("en", { style: "currency", currency })
        .resolvedOptions().maximumFractionDigits;
      return new Intl.NumberFormat("en", { style: "currency", currency })
        .format(Number(value) / 10 ** digits);
    } catch {
      return `${Number(value).toLocaleString()} ${currency}`;
    }
  }
  if (typeof value === "number") return value.toLocaleString();
  if (/^\d{4}-\d\d-\d\dT/.test(String(value))) return String(value).slice(0, 10);
  return String(value);
}

/**
 * How an answer is drawn, and why, so a screen can say so.
 *
 * - no dimension: a figure per measure, not a chart of one bar
 * - one dimension: a chart with a series per measure
 * - two dimensions and one measure: the second dimension becomes the series,
 *   up to four of them, and a table beyond that rather than a fifth colour
 * - money grouped by its own currency: a table, because one axis would put
 *   dollars and yen on one scale
 */
export function shape(result, state) {
  const cols = result?.columns || [];
  const dims = cols.filter((c) => c.role === "dimension");
  const measures = cols.filter((c) => c.role === "measure");
  const rows = result?.rows || [];
  const table = (why) => ({ kind: "table", why, columns: cols, rows });

  if (!rows.length) return { kind: "empty", columns: cols, rows };
  if (!dims.length) return { kind: "figures", measures, row: rows[0] };

  const wantTable = state?.chart === "table";
  if (wantTable) return table("");

  const moneyByItsCurrency = (d) => measures.some((m) => isMoney(m) && m.currency_field === d.key);

  if (dims.length === 1) {
    const [d] = dims;
    if (moneyByItsCurrency(d)) {
      return table("Money in different currencies is not drawn on one scale, so it is a table.");
    }
    const sharedCurrency = measures.find((m) => isMoney(m) && m.currency)?.currency;
    return {
      kind: "chart",
      time: d.type === "time",
      dimension: { key: d.key, label: d.label },
      series: measures.map((m) => ({ key: m.key, label: m.label })),
      rows,
      valueFormat: measures.some(isMoney)
        ? { style: "money", currency: sharedCurrency, minorUnits: true } : undefined,
      measures,
    };
  }

  if (dims.length === 2 && measures.length === 1) {
    const [d1, d2] = dims;
    const [m] = measures;
    const values = [...new Set(rows.map((r) => r[d2.key]))];
    if (values.length > MAX_SERIES) {
      return table(`That is ${values.length} groups, which is more than a chart can tell apart, so it is a table.`);
    }
    const series = values.map((v, i) => ({
      key: `s${i}`, label: String(v),
      valueFormat: isMoney(m) && m.currency_field === d2.key && v !== NONE_LABEL
        ? { style: "money", currency: v, minorUnits: true } : undefined,
    }));
    const byFirst = new Map();
    for (const r of rows) {
      const at = byFirst.get(r[d1.key]) || { [d1.key]: r[d1.key] };
      at[`s${values.indexOf(r[d2.key])}`] = r[m.key];
      byFirst.set(r[d1.key], at);
    }
    return {
      kind: "chart",
      time: d1.type === "time",
      dimension: { key: d1.key, label: d1.label },
      // The second dimension is what the series are, so a mark names it by the
      // series it is in and not by anything in the row.
      splitBy: { key: d2.key, label: d2.label },
      series,
      rows: [...byFirst.values()],
      valueFormat: isMoney(m) && m.currency_field !== d2.key
        ? { style: "money", currency: m.currency, minorUnits: true } : undefined,
      measures,
    };
  }

  return table("Three things at once is easier to read as a table.");
}

/** `bar` or `line` for a chart the form asked about, `auto` choosing by shape. */
export function chartKind(shaped, state) {
  if (state?.chart === "bar" || state?.chart === "line") return state.chart;
  return shaped.time ? "line" : "bar";
}

// -- the starting page --------------------------------------------------------------------

/** One count per type the kernel serves, from the schema and nothing else. */
export function overviewSpecs(schema) {
  return typesOf(schema).map((t) => ({
    type: t.type,
    label: t.label || t.type,
    spec: { type: t.type, measures: [{ fn: "count" }] },
  }));
}

// -- saved in this browser ------------------------------------------------------------------

const KEY = "insights.reports.v1";

function store(storage) {
  try {
    return storage || globalThis.localStorage || null;
  } catch {
    return null;
  }
}

export function loadSaved(owner, storage) {
  const s = store(storage);
  if (!s) return [];
  try {
    const list = JSON.parse(s.getItem(`${KEY}:${owner}`) || "[]");
    return Array.isArray(list) ? list.filter((r) => r && r.id && r.name && r.state) : [];
  } catch {
    return [];
  }
}

function write(owner, list, storage) {
  const s = store(storage);
  if (!s) return false;
  try {
    s.setItem(`${KEY}:${owner}`, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

export function saveReport(owner, name, state, storage, now = () => new Date()) {
  const list = loadSaved(owner, storage);
  const entry = {
    id: `r${now().getTime().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    name: String(name).trim() || "Untitled",
    state,
    savedAt: now().toISOString(),
  };
  return write(owner, [entry, ...list], storage) ? entry : null;
}

export function deleteReport(owner, id, storage) {
  return write(owner, loadSaved(owner, storage).filter((r) => r.id !== id), storage);
}
